(ns hai.ui.toast
  (:require
   [garden.stylesheet]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [defevent-fx defeffect]]
   [lambdaisland.ornament :as o]
   [reagent.core :as reagent]))

(def !state (reagent/atom []))

;; Dismiss timers live outside `!state`, so pausing one on hover doesn't
;; re-render the toasts. Each entry holds the running timeout's handle plus how
;; much of the toast's time is left and when that stretch started — so leaving
;; the pointer resumes the remainder instead of restarting the full timeout, and
;; a toast read halfway doesn't get a fresh lease every time the pointer crosses
;; it.
(defonce ^:private !timers (atom {}))

;; `filterv`, not `remove`: a lazy seq here would leave `!state` a seq rather
;; than a vector, and the next `add!`'s `conj` would prepend — silently flipping
;; the stacking order after the first dismissal.
(defn remove! [{:keys [id]}]
  (swap! !timers dissoc id)
  (swap! !state #(filterv (fn [t] (not= (:id t) id)) %)))

(defn- schedule-dismiss!
  "Dismiss `toast` in `ms`, keeping the handle so it can be paused."
  [{:keys [id] :as toast} ms]
  (swap! !timers assoc id
         {:handle     (js/setTimeout #(remove! toast) ms)
          :remaining  ms
          :started-at (js/Date.now)}))

(defn pause-dismiss!
  "Hold a toast open. Whatever time it had left is banked for
   [[resume-dismiss!]]; already-paused and already-gone toasts are no-ops."
  [{:keys [id]}]
  (when-let [{:keys [handle remaining started-at]} (get @!timers id)]
    (when handle
      (js/clearTimeout handle)
      (swap! !timers assoc id
             {:handle     nil
              :remaining  (max 0 (- remaining (- (js/Date.now) started-at)))
              :started-at nil}))))

(defn resume-dismiss!
  "Let a paused toast go again, on the time it had left when it was paused."
  [{:keys [id] :as toast}]
  (when-let [{:keys [handle remaining]} (get @!timers id)]
    (when-not handle
      (schedule-dismiss! toast remaining))))

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules toast-keyframes
  (garden.stylesheet/at-keyframes
   :toast-slide-up
   [:from {:transform "translateY(12px)" :opacity "0"}]
   [:to   {:transform "translateY(0)"    :opacity "1"}]))

(o/defstyled Toast :div
  {:display        "flex"
   :flex-direction "row"
   :align-items    "flex-start"
   :gap            "12px"
   :max-width      "340px"
   :padding        "15px 18px 16px"
   :border-radius  "3px"
   :background     "#3d5560"
   :color          "#f6efdd"
   :font-family    "'Geist', system-ui, -apple-system, BlinkMacSystemFont, sans-serif"
   :font-size      "14px"
   :font-weight    "500"
   :line-height    "1.5"
   :box-shadow     "0 1px 1px rgba(60,40,20,0.06), 0 12px 26px rgba(60,40,20,0.18)"
   :animation      "toast-slide-up 220ms cubic-bezier(.2,.7,.3,1) both"
   :pointer-events "auto"}
  [:&.success {:background "#4f6d35"}]
  [:&.error   {:background "#9a4128"}]
  [:.icon
   {:flex            "0 0 auto"
    :margin-top      "2px"
    :color           "#f6efdd"
    :display         "inline-flex"
    :align-items     "center"
    :justify-content "center"}
   [:svg
    {:width           "15px"
     :height          "15px"
     :stroke          "currentColor"
     :stroke-width    "2"
     :stroke-linecap  "round"
     :stroke-linejoin "round"
     :fill            "none"}]]
  [:.text
   {:flex       "1"
    :min-width  "0"
    :word-break "break-word"}
   [:.action
    {:margin-left     "6px"
     :white-space     "nowrap"
     :color           "inherit"
     :font-weight     "600"
     :text-decoration "underline"
     :cursor          "pointer"}
    [:&:hover {:opacity "0.8"}]
    ;; The "opens in a new tab" arrow — only rendered for `_blank` links (the
    ;; extension), and only surfaced while hovering the link.
    [:.action-icon
     {:display        "inline-flex"
      :vertical-align "middle"
      :margin-left    "3px"
      :visibility     "hidden"}
     [:svg {:width "13px" :height "13px" :fill "currentColor"}]]
    [:&:hover [:.action-icon {:visibility "visible"}]]]]
  ([{:keys [text level action] :as toast}]
   (let [klass (case level
                 :success "success"
                 :error   "error"
                 "info")
         icon  (case level
                 :success
                 [:svg {:viewBox "0 0 24 24"}
                  [:polyline {:points "20 6 9 17 4 12"}]]
                 :error
                 [:svg {:viewBox "0 0 24 24"}
                  [:line {:x1 "6"  :y1 "6" :x2 "18" :y2 "18"}]
                  [:line {:x1 "18" :y1 "6" :x2 "6"  :y2 "18"}]]
                 ;; info — default for any non-success/error level
                 [:svg {:viewBox "0 0 24 24"}
                  [:circle {:cx "12" :cy "12" :r "9"}]
                  [:circle {:cx "12" :cy "7.5" :r "1.2"
                            :fill "currentColor" :stroke "none"}]
                  [:line {:x1 "12" :y1 "11" :x2 "12" :y2 "16"}]])]
     [:<> {:class klass
           ;; Reading one shouldn't be a race: the pointer holds a toast open,
           ;; and so does keyboard focus — React's focus events bubble, so
           ;; tabbing to the action link counts too.
           :on-mouse-enter #(pause-dismiss! toast)
           :on-mouse-leave #(resume-dismiss! toast)
           :on-focus       #(pause-dismiss! toast)
           :on-blur        #(resume-dismiss! toast)}
      [:span.icon icon]
      [:div.text text
       (when-let [{:keys [label href target]} action]
         [:a.action {:href href :target target :rel "noopener noreferrer"}
          label
          (when (= target "_blank")
            [:span.action-icon [icons/outlined-north_east]])])]])))

(o/defstyled ToastWrapper :div
  {:position       "fixed"
   :bottom         "16px"
   :right          "16px"
   :z-index        "9999"
   :display        "flex"
   :flex-direction "column"
   :gap            "10px"
   :pointer-events "none"}
  ([]
   ;; A live region, so toasts are announced rather than only seen. Polite (not
   ;; assertive) because toasts confirm rather than interrupt, and without
   ;; aria-atomic so adding one announces just that toast, not every toast
   ;; currently on screen.
   [:<>
    {:role "status" :aria-live "polite"}
    (doall
     (for [{:keys [id] :as toast} @!state]
       ^{:key id}
       [Toast toast]))]))

(defn create [{:keys [text level timeout action]
               :or {level :info
                    timeout 6000}}]
  {:id (random-uuid)
   :text text
   :timeout timeout
   :level level
   :action action})

(defn add! [opts]
  (let [toast (create opts)]
    (swap! !state conj toast)
    (schedule-dismiss! toast (:timeout toast))))

(defn success! [opts] (add! (assoc opts :level :success)))
(defn info!    [opts] (add! (assoc opts :level :info)))
(defn error!   [opts] (add! (assoc opts :level :error)))

(defeffect :toast/add [opts]
  (add! opts))

(defevent-fx :toast/add [{:keys [db]} opts]
  {:toast/add (assoc opts :level :info)})

(defevent-fx :toast/success [{:keys [db]} opts]
  {:toast/add (assoc opts :level :success)})

(defevent-fx :toast/info [{:keys [db]} opts]
  {:toast/add (assoc opts :level :info)})

(defevent-fx :toast/error [{:keys [db]} opts]
  {:toast/add (assoc opts :level :error)})
